import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { User } from '@aptsi-types';
import { KeycloakService } from 'keycloak-angular';
import { DataAssetMappingService, ImplementationDetails } from '../../shared/services/data-protection-program.service';
import { UserService } from 'src/app/features/user-management/users/user.service';
import { KeycloakStateService } from 'src/app/core/service/utility.service';
import { ProcessCompleteDialogComponent } from 'src/app/shared/dialogs/process-complete-dialog/process-complete-dialog.component';
import { MatDialog } from '@angular/material/dialog';

type WizardStep = 1 | 2 | 3;
type RecordLike = Record<string, unknown>;
type OwnerTarget = 'programOwner' | 'implementationOwner' | 'verifiedBy';
type ProgramUser = User & Record<string, unknown>;

const ALGORITHM_CODE_BY_LABEL: Record<string, string> = {
  None: 'NONE',
  'Longest Common Subsequence (LCS)': 'LONGEST_COMMON_SUBSEQUENCE',
  'Fixed-length': 'FIXED_LENGTH',
  'SHA3-256': 'SHA3_256',
  'SHA3-512': 'SHA3_512',
  Substitution: 'SUBSTITUTION',
  Shuffling: 'SHUFFLING',
  Pseudonymization: 'PSEUDONYMIZATION',
  FF3x: 'FF3X',
  'AES-256': 'AES_256',
  'AES-512': 'AES_512',
  Homomorphic: 'HOMOMORPHIC',
  Metamorphic: 'METAMORPHIC',
  Crypto_Shuffle: 'CRYPTO_SHUFFLE',
};

@Component({
  selector: 'app-data-protection-program-process',
  templateUrl: './data-protection-program-process.component.html',
  styleUrls: ['./data-protection-program-process.component.scss'],
})
export class DataProtectionProgramProcessComponent implements OnInit {
  readonly steps: Array<{ id: WizardStep; label: string; icon: string }> = [
    { id: 1, label: 'Program Details', icon: 'bi-card-text' },
    { id: 2, label: 'Implementation', icon: 'bi-shield-check' },
    { id: 3, label: 'Verification', icon: 'bi-patch-check' },
  ];

  readonly protectionTypeOptions = [
    'TRUNCATE',
    'HASH',
    'MASK',
    'ANONYMIZE',
    'TOKENIZE',
    'ENCRYPTION',
    'POLYMORPHIC_ENCRYPTION',
    'None',
  ];

  readonly protectionTypeAlgorithmMapping = ALGORITHM_CODE_BY_LABEL;

  private readonly allProtectionTypeAlgorithms: Record<string, string[]> = {
    TRUNCATE: ['Longest Common Subsequence (LCS)', 'Fixed-length', 'None'],
    HASH: ['SHA3-256', 'SHA3-512', 'None'],
    MASK: ['Substitution', 'Shuffling', 'None'],
    ANONYMIZE: ['Pseudonymization', 'None'],
    TOKENIZE: ['FF3x', 'None'],
    ENCRYPTION: ['AES-256', 'AES-512', 'Homomorphic', 'Metamorphic', 'None'],
    POLYMORPHIC_ENCRYPTION: ['Crypto_Shuffle', 'None'],
    None: [],
  };

  readonly applicableStandardsOptions = [
    'California Consumer Privacy Act (CCPA / CPRA)',
    'General Data Protection Regulation (GDPR)',
    'Payment Card Industry Data Security Standard (PCI DSS)',
    'Health Insurance Portability and Accountability Act (HIPAA)',
    'Personal Information Protection and Electronic Documents Act (PIPEDA)',
    'ISO/IEC 27001',
    'NIST SP 800-53',
  ];

  form: FormGroup;
  currentStep: WizardStep = 1;
  mappingId = '';
  programId = '';

  hasProgramDetails = false;
  hasImplementationDetails = false;
  hasVerificationDetails = false;

  isLoading = true;
  isSubmitting = false;
  pageError = '';
  submitError = '';
  submitSuccess = false;
  successMessage = '';
  stepWarning = '';


  // User-selection popup state. Owner controls store username and Keycloak user ID.
  userPickerOpen = false;
  userPickerTarget: OwnerTarget = 'programOwner';
  userPickerTitle = 'Select user';
  users: ProgramUser[] = [];
  filteredUsers: ProgramUser[] = [];
  userSearchText = '';
  isLoadingUsers = false;
  userLoadError = '';
  private usersLoaded = false;

  private existingProgram: RecordLike | null = null;
  private existingImplementation: RecordLike | null = null;
  private existingVerification: RecordLike | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly programService: DataAssetMappingService,
    private readonly userService: UserService,
    private readonly keycloakStateService: KeycloakStateService,
    private readonly keycloakService: KeycloakService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly dialog: MatDialog
  ) {
    this.form = this.fb.group({
      // Step 1: Program details
      name: ['', [Validators.required, Validators.maxLength(160)]],
      description: ['', [Validators.maxLength(2000)]],
      programStartDate: ['', Validators.required],
      targetCompletionDate: ['', Validators.required],
      programOwner: ['', Validators.required],
      programOwnerKeycloakId: ['', Validators.required],
      // Hidden persistence field: update API expects the existing program status.
      programStatus: ['IN_PROGRESS'],

      // Step 2: Implementation details
      implementationStatus: ['IN_PROGRESS', Validators.required],
      protectionType: ['', Validators.required],
      algorithmOrMethod: [''],
      applicableStandards: [[], Validators.required],
      implementationOwner: ['', Validators.required],
      implementationOwnerKeycloakId: ['', Validators.required],
      plannedStartDate: [''],
      plannedCompletionDate: [''],
      actualImplementationDate: [''],
      implementationNotes: ['', Validators.maxLength(3000)],
      implementationEvidenceUri: ['', Validators.maxLength(2000)],

      // Step 3: Verification details
      verificationStatus: ['PASSED'],
      verifiedBy: [''],
      verifiedByKeycloakId: [''],
      verificationDate: [''],
      verificationResult: [''],
      verificationEvidenceUri: ['', Validators.maxLength(2000)],
    });

    this.form.get('targetCompletionDate')?.setValidators([
      Validators.required,
      (control: AbstractControl) => this.validateTargetCompletionDate(control),
    ]);

    // Keep calendar limits and cross-field validation synchronized with date changes.
    this.form.get('programStartDate')?.valueChanges.subscribe(() => this.onProgramStartDateChanged());
    this.form.get('targetCompletionDate')?.valueChanges.subscribe(() => this.onProgramTargetCompletionDateChanged());

    this.refreshProgramDateValidators();
    this.configureImplementationDateValidators();
  }

  ngOnInit(): void {
    this.mappingId =
      this.route.snapshot.paramMap.get('mappingId') ??
      this.route.snapshot.paramMap.get('id') ??
      this.route.snapshot.paramMap.get('mappingID') ??
      this.route.snapshot.queryParamMap.get('mappingId') ??
      this.route.snapshot.queryParamMap.get('id') ??
      '';

    if (!this.mappingId.trim()) {
      this.isLoading = false;
      this.pageError = 'The mapping ID is missing from the route. Open this screen from a mapped data asset.';
      return;
    }

    void this.loadExistingProgram();
  }

  get algorithmOptions(): string[] {
    const protectionType = this.asString(this.form.get('protectionType')?.value);
    return this.allProtectionTypeAlgorithms[protectionType] ?? [];
  }

  get selectedAlgorithmIsRequired(): boolean {
    return this.algorithmOptions.length > 0;
  }

  get isImplementationInProgress(): boolean {
    return this.asString(this.form.get('implementationStatus')?.value) === 'IN_PROGRESS';
  }

  get canProceedToVerification(): boolean {
    return this.asString(this.form.get('implementationStatus')?.value) === 'IMPLEMENTED';
  }

  get todayDate(): string {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${today.getFullYear()}-${month}-${day}`;
  }

  get programStartDateValue(): string {
    return this.asString(this.form.get('programStartDate')?.value);
  }

  get targetCompletionDateValue(): string {
    return this.asString(this.form.get('targetCompletionDate')?.value);
  }

  /** Target completion must be at least one calendar day after program start. */
  get minimumTargetCompletionDate(): string {
    return this.programStartDateValue ? this.addDaysToDateString(this.programStartDateValue, 1) : '';
  }

  get plannedCompletionMinDate(): string {
    const programStart = this.programStartDateValue;
    const plannedStart = this.asString(this.form.get('plannedStartDate')?.value);
    if (!plannedStart) return programStart;
    return plannedStart > programStart ? plannedStart : programStart;
  }

  get selectedApplicableStandards(): string[] {
    const value: unknown = this.form.get('applicableStandards')?.value;
    return Array.isArray(value) ? value.map(item => this.asString(item)).filter(Boolean) : [];
  }

  get isUpdatingAnyStep(): boolean {
    return this.hasProgramDetails || this.hasImplementationDetails || this.hasVerificationDetails;
  }

  get pageTitle(): string {
    return this.hasProgramDetails ? 'Update Data Protection Program' : 'Create Data Protection Program';
  }

  get currentStepHeading(): string {
    if (this.currentStep === 1) return this.hasProgramDetails ? 'Update Program Details' : 'Add Program Details';
    if (this.currentStep === 2) return this.hasImplementationDetails ? 'Update Implementation Details' : 'Add Implementation Details';
    return this.hasVerificationDetails ? 'Update Verification Details' : 'Add Verification Details';
  }

  async loadExistingProgram(): Promise<void> {
    this.isLoading = true;
    this.pageError = '';

    try {
      const programResponse: unknown = await this.programService
        .FetchProgramDetailsByMappingId(this.mappingId)
        .toPromise();
      const program = this.findEntity(programResponse, [
        'programId', 'name', 'programOwner', 'programStartDate', 'targetCompletionDate',
      ]);

      if (!program) {
        // An empty result means Step 1 is a create operation.
        this.hasProgramDetails = false;
        this.hasImplementationDetails = false;
        this.hasVerificationDetails = false;
        this.programId = '';
        this.existingProgram = null;
        this.applyImplementationStatusRules();
        return;
      }

      this.hasProgramDetails = true;
      this.existingProgram = program;
      this.programId = this.asString(program['programId'] ?? program['id']);
      this.patchProgramDetails(program);

      if (!this.programId) {
        throw new Error('Program details were returned without a programId.');
      }

      // Empty response on either child step means that step independently uses Create.
      // const [implementationResponse, verificationResponse] = await Promise.all([
      //   this.programService.FetchImplementationDetailsByProgramId(this.programId).toPromise(),
      //   this.programService.FetchVerificationDetailsByProgramId(this.programId).toPromise(),
      // ]);

      // Fetch implementation and verification details with a 400 ms stagger.
const [implementationResponse, verificationResponse] = await Promise.all([
  this.programService
    .FetchImplementationDetailsByProgramId(this.programId)
    .toPromise(),

  this.delay(400).then(() =>
    this.programService
      .FetchVerificationDetailsByProgramId(this.programId)
      .toPromise()
  ),
]);

      const implementation = this.findEntity(implementationResponse, [
        'implementationId', 'implementationStatus', 'protectionType', 'algorithmOrMethod',
        'implementationOwner', 'plannedStartDate', 'actualImplementationDate',
      ]);
      const verification = this.findEntity(verificationResponse, [
        'verificationId', 'verificationStatus', 'verifiedBy', 'verificationDate', 'verificationResult',
      ]);

      this.hasImplementationDetails = !!implementation;
      this.hasVerificationDetails = !!verification;
      this.existingImplementation = implementation;
      this.existingVerification = verification;

      if (implementation) this.patchImplementationDetails(implementation);
      if (verification) this.patchVerificationDetails(verification);
    } catch (error) {
      console.error('Unable to load data protection program details:', error);
      this.pageError = 'Unable to load program details. Please check the mapping ID and try again.';
    } finally {
      this.isLoading = false;
      this.refreshView();
    }
  }

  /**
 * Delays execution for the specified duration.
 */
private delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

  async nextStep(): Promise<void> {
    this.submitError = '';
    this.stepWarning = '';

    if (this.currentStep === 1) {
      if (!this.validateControls([
        'name', 'programStartDate', 'targetCompletionDate', 'programOwner', 'programOwnerKeycloakId',
      ])) return;

      this.isSubmitting = true;
      try {
        await this.saveProgramDetails();
        this.currentStep = 2;
      } catch (error) {
        this.handleSaveError('program details', error);
      } finally {
        this.isSubmitting = false;
        this.refreshView();
      }
      return;
    }

    if (this.currentStep === 2) {
      const implementationFields = [
        'implementationStatus', 'protectionType', 'implementationOwner', 'implementationOwnerKeycloakId',
        'applicableStandards',
      ];
      if (this.selectedAlgorithmIsRequired) implementationFields.push('algorithmOrMethod');
      if (this.isImplementationInProgress) {
        implementationFields.push('plannedStartDate', 'plannedCompletionDate');
      } else {
        implementationFields.push('actualImplementationDate');
      }
      if (!this.validateControls(implementationFields)) return;

      this.isSubmitting = true;
      try {
        await this.saveImplementationDetails();

        // Persist the implementation step even when it is still in progress, but
        // keep the user on Step 2 until the status is explicitly IMPLEMENTED.
        if (!this.canProceedToVerification) {
          this.stepWarning = 'Implementation details were saved. Change Implementation Status to Implemented before proceeding to verification.';
          return;
        }

        this.currentStep = 3;
      } catch (error) {
        this.handleSaveError('implementation details', error);
      } finally {
        this.isSubmitting = false;
        this.refreshView();
      }
    }
  }

  previousStep(): void {
    this.submitError = '';
    if (this.currentStep > 1) this.currentStep = (this.currentStep - 1) as WizardStep;
  }

  async finish(): Promise<void> {
    this.submitError = '';
    const verificationFields = this.hasVerificationDetails
      ? ['verificationResult']
      : ['verificationStatus', 'verifiedBy', 'verifiedByKeycloakId', 'verificationDate', 'verificationResult'];
    if (!this.validateControls(verificationFields)) return;

    const verificationWasExisting = this.hasVerificationDetails;
    this.isSubmitting = true;
    try {
      await this.saveVerificationDetails();
      this.submitSuccess = true;
      this.successMessage = verificationWasExisting
        ? 'Verification details updated successfully.'
        : 'Verification details created successfully.';
      // Keep the finished screen visible; the user can explicitly return to the listing.
    } catch (error) {
      this.handleSaveError('verification details', error);
    } finally {
      this.isSubmitting = false;
      this.refreshView();
    }
  }

  async retryLoad(): Promise<void> {
    await this.loadExistingProgram();
  }

  onImplementationStatusChanged(): void {
    this.stepWarning = '';
    this.submitError = '';
    this.configureImplementationDateValidators();
  }

  onProtectionTypeChanged(): void {
    const control = this.form.get('algorithmOrMethod');
    const options = this.algorithmOptions;
    if (options.length) {
      control?.setValue('');
      control?.setValidators([Validators.required]);
    } else {
      control?.setValue('None');
      control?.clearValidators();
    }
    control?.updateValueAndValidity({ emitEvent: false });
  }

  onProgramStartDateChanged(): void {
    const startDate = this.programStartDateValue;
    const targetControl = this.form.get('targetCompletionDate');

    if (startDate) {
      const earliestTarget = this.addDaysToDateString(startDate, 1);
      const currentTarget = this.asString(targetControl?.value);
      if (!currentTarget || currentTarget < earliestTarget) {
        // Default to the first allowed day; the user can choose a later date from the calendar.
        targetControl?.setValue(earliestTarget, { emitEvent: false });
      }
    }

    this.refreshProgramDateValidators();
  }

  onProgramTargetCompletionDateChanged(): void {
    this.refreshProgramDateValidators();
  }

  /** Prevent typing/pasting a date; dates are selected through the native calendar UI. */
  preventManualDateEntry(event: KeyboardEvent): void {
    const allowedKeys = ['Tab', 'Shift', 'Escape', 'Enter'];
    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  preventDatePaste(event: ClipboardEvent): void {
    event.preventDefault();
  }

  toggleApplicableStandard(standard: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const control = this.form.get('applicableStandards');
    const selected = this.selectedApplicableStandards;
    const next = checked
      ? (selected.includes(standard) ? selected : [...selected, standard])
      : selected.filter(item => item !== standard);

    control?.setValue(next);
    control?.markAsTouched();
    control?.markAsDirty();
    control?.updateValueAndValidity({ emitEvent: false });
  }

  async openUserPicker(target: OwnerTarget): Promise<void> {
    this.userPickerTarget = target;
    this.userPickerTitle = target === 'programOwner'
      ? 'Select Program Owner'
      : target === 'implementationOwner'
        ? 'Select Implementation Owner'
        : 'Select Verifier';
    this.userPickerOpen = true;
    this.userSearchText = '';
    this.userLoadError = '';

    if (!this.usersLoaded) {
      await this.loadUsers();
    } else {
      this.applyUserFilter();
    }
    this.refreshView();
  }

  closeUserPicker(): void {
    this.userPickerOpen = false;
    this.userLoadError = '';
  }

  onUserSearch(event: Event): void {
    this.userSearchText = (event.target as HTMLInputElement).value;
    this.applyUserFilter();
  }

  userInitial(user: ProgramUser): string {
    const first = this.asString(user['firstName'] ?? user['username'] ?? '?');
    return first ? first.charAt(0).toUpperCase() : '?';
  }

  selectUser(user: ProgramUser): void {
    const username = this.asString(user['username']);
    const userId = this.asString(user['userId'] ?? user['id']);
    if (!username || !userId) {
      this.userLoadError = 'The selected user is missing a username or Keycloak user ID.';
      return;
    }

    if (this.userPickerTarget === 'programOwner') {
      this.form.patchValue({ programOwner: username, programOwnerKeycloakId: userId });
    } else if (this.userPickerTarget === 'implementationOwner') {
      this.form.patchValue({ implementationOwner: username, implementationOwnerKeycloakId: userId });
    } else {
      this.form.patchValue({ verifiedBy: username, verifiedByKeycloakId: userId });
    }

    this.closeUserPicker();
    this.refreshView();
  }

  returnToProgramList(): void {
    void this.router.navigate(['/data-protection/auth-tools/data-protection-program']);
  }

  private async saveProgramDetails(): Promise<void> {
    const values = this.form.getRawValue();
    const createdBy = await this.getCurrentUsername();
    const createdByKeycloakId = this.getCurrentKeycloakUserId();
    if (!createdBy || !createdByKeycloakId) {
      throw new Error('Unable to determine the current Keycloak username and user ID for audit fields.');
    }

    if (this.hasProgramDetails && this.programId) {
      await this.programService.UpdateProgramDetails(
        this.programId,
        this.asString(values.name).trim(),
        this.asString(values.description).trim(),
        this.asString(values.programStartDate),
        this.asString(values.targetCompletionDate),
        this.asString(values.programOwner),
        this.asString(values.programOwnerKeycloakId),
        this.asString(values.programStatus) || this.asString(this.existingProgram?.['programStatus']) || 'IN_PROGRESS',
      ).toPromise();
      return;
    }

    const response: unknown = await this.programService.CreateProgramDetails(
      this.mappingId,
      this.asString(values.name).trim(),
      this.asString(values.description).trim(),
      this.asString(values.programStartDate),
      this.asString(values.targetCompletionDate),
      this.asString(values.programOwner),
      this.asString(values.programOwnerKeycloakId),
      createdBy,
      createdByKeycloakId,
    ).toPromise();

    let createdRecord = this.findEntity(response, ['programId', 'name', 'programOwner']);
    if (createdRecord) this.existingProgram = createdRecord;
    let newProgramId = this.asString(createdRecord?.['programId'] ?? createdRecord?.['id']);
    if (!newProgramId) {
      const fetched: unknown = await this.programService.FetchProgramDetailsByMappingId(this.mappingId).toPromise();
      createdRecord = this.findEntity(fetched, ['programId', 'name', 'programOwner']);
      newProgramId = this.asString(createdRecord?.['programId'] ?? createdRecord?.['id']);
      if (createdRecord) {
        this.existingProgram = createdRecord;
        this.form.patchValue({ programStatus: createdRecord['programStatus'] ?? 'IN_PROGRESS' });
      }
    }

    if (!newProgramId) {
      throw new Error('Program creation returned no programId. The program was submitted, but implementation cannot continue until a programId is available.');
    }

    this.programId = newProgramId;
    this.hasProgramDetails = true;
    this.submitSuccess = false;
  }

  private async saveImplementationDetails(): Promise<void> {
    if (!this.programId) throw new Error('A programId is required before implementation details can be saved.');

    const values = this.form.getRawValue();
    const implementationStatus = this.asString(values.implementationStatus);
    const protectionType = this.asString(values.protectionType);
    const algorithmLabel = this.asString(values.algorithmOrMethod);
    const body: ImplementationDetails = {
      programId: this.programId,
      implementationStatus,
      protectionType,
      algorithmOrMethod: this.toAlgorithmCode(algorithmLabel),
      applicableStandards: Array.isArray(values.applicableStandards)
        ? (values.applicableStandards as unknown[]).map(value => this.asString(value)).filter(Boolean)
        : [],
      implementationOwner: this.asString(values.implementationOwner),
      implementationOwnerKeycloakId: this.asString(values.implementationOwnerKeycloakId),
      implementationNotes: this.asString(values.implementationNotes).trim() || undefined,
      implementationEvidenceUri: this.asString(values.implementationEvidenceUri).trim() || undefined,
    };

    // IN_PROGRESS uses planned dates. IMPLEMENTED uses the actual date.
    // Do not include fields that the respective endpoint explicitly disallows.
    if (implementationStatus === 'IMPLEMENTED') {
      body.actualImplementationDate = this.asString(values.actualImplementationDate);
      delete body.plannedStartDate;
      delete body.plannedCompletionDate;
    } else {
      body.plannedStartDate = this.asString(values.plannedStartDate);
      body.plannedCompletionDate = this.asString(values.plannedCompletionDate);
      delete body.actualImplementationDate;
    }

    if (this.hasImplementationDetails) {
      if (implementationStatus === 'IMPLEMENTED') {
        await this.programService.UpdateImplementationDetailsForImplementedStatus(body).toPromise();
      } else {
        await this.programService.UpdateImplementationDetailsForNotImplementedStatus(body).toPromise();
      }
    } else {
      // Every create operation sends creator audit fields from the current Keycloak session.
      const createdBy = await this.getCurrentUsername();
      const createdByKeycloakId = this.getCurrentKeycloakUserId();
      if (!createdBy || !createdByKeycloakId) {
        throw new Error('Unable to determine the current Keycloak username and user ID for audit fields.');
      }
      body.createdBy = createdBy;
      body.createdByKeycloakId = createdByKeycloakId;

      if (implementationStatus === 'IMPLEMENTED') {
        await this.programService.CreateImplementationDetailsForImplementedStatus(body).toPromise();
      } else {
        await this.programService.CreateImplementationDetailsForNotImplementedStatus(body).toPromise();
      }
      this.hasImplementationDetails = true;
    }

    this.existingImplementation = { ...body } as RecordLike;
    this.submitSuccess = false;
  }

  private async saveVerificationDetails(): Promise<void> {
    if (!this.programId) throw new Error('A programId is required before verification details can be saved.');
    const values = this.form.getRawValue();

    if (this.hasVerificationDetails) {
      await this.programService.UpdateerificationDetails(
        this.programId,
        this.asString(values.verificationResult).trim(),
        this.asString(values.verificationEvidenceUri).trim(),
      ).toPromise();
    } else {
      const createdBy = await this.getCurrentUsername();
      const createdByKeycloakId = this.getCurrentKeycloakUserId();
      if (!createdBy || !createdByKeycloakId) {
        throw new Error('Unable to determine the current Keycloak username and user ID for audit fields.');
      }

      await this.programService.CreateVerificationDetails(
        this.programId,
        this.asString(values.verificationStatus),
        this.asString(values.verifiedBy),
        this.asString(values.verifiedByKeycloakId),
        this.asString(values.verificationDate),
        this.asString(values.verificationResult).trim(),
        this.asString(values.verificationEvidenceUri).trim(),
        createdBy,
        createdByKeycloakId,
      ).toPromise();

      await this.programService.UpdateProgramDetails(
                 this.programId,
        this.asString(values.name).trim(),
        this.asString(values.description).trim(),
        this.asString(values.programStartDate),
        this.asString(values.targetCompletionDate),
        this.asString(values.programOwner),
        this.asString(values.programOwnerKeycloakId),
        'COMPLETED',
      ).toPromise();

      this.showProcessCompleteDialog();

      this.router.navigate(['data-protection/auth-tools/data-protection-program']);
      
      this.hasVerificationDetails = true;
    }

    

    this.existingVerification = {
      verificationStatus: values.verificationStatus,
      verifiedBy: values.verifiedBy,
      verifiedByKeycloakId: values.verifiedByKeycloakId,
      verificationDate: values.verificationDate,
      verificationResult: values.verificationResult,
      verificationEvidenceUri: values.verificationEvidenceUri,
    };
  }


  showProcessCompleteDialog(): void {
  this.dialog.open(ProcessCompleteDialogComponent, {
    data: {
      title: 'Data Protection Program Completed',
      message: 'The data protection program has completed successfully.',
    },
      width: 'min(32rem, 94vw)',
      maxWidth: '94vw',
      autoFocus: 'first-tabbable',
      restoreFocus: true,
    });
  }

  async loadUsers(): Promise<void> {
    const organizationId = this.keycloakStateService.getOrganizationId();
    if (!organizationId) {
      this.userLoadError = 'Organization ID is unavailable. Please sign in again or refresh your session.';
      return;
    }

    this.isLoadingUsers = true;
    this.userLoadError = '';
    try {
      const firstPageRaw: unknown = await this.userService.getUsers(0, organizationId).toPromise();
      const firstPage = this.toRows(firstPageRaw);
      const firstUser = firstPage[0];
      const totalPages = Math.max(1, this.readNumber(firstUser?.['totalPages'], 1));
      const allRows: RecordLike[] = [...firstPage];

      for (let page = 1; page < totalPages; page += 1) {
        const pageRaw: unknown = await this.userService.getUsers(page, organizationId).toPromise();
        allRows.push(...this.toRows(pageRaw));
      }

      const seen = new Set<string>();
      this.users = allRows
        .filter(row => {
          const id = this.asString(row['userId'] ?? row['id']);
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        })
        .map(row => row as unknown as ProgramUser);
      this.usersLoaded = true;
      this.applyUserFilter();
      if (!this.users.length) this.userLoadError = 'No users were returned for this organization.';
    } catch (error) {
      console.error('Unable to load users for the owner picker:', error);
      this.userLoadError = 'Unable to load users. Please try again.';
    } finally {
      this.isLoadingUsers = false;
      this.refreshView();
    }
  }

  private applyUserFilter(): void {
    const query = this.userSearchText.trim().toLowerCase();
    this.filteredUsers = !query
      ? [...this.users]
      : this.users.filter(user => [
          user['username'], user['firstName'], user['lastName'], user['email'], user['roleName'],
        ].some(value => this.asString(value).toLowerCase().includes(query)));
  }

  private patchProgramDetails(program: RecordLike): void {
    this.form.patchValue({
      name: program['name'] ?? '',
      description: program['description'] ?? '',
      programStartDate: this.dateInputValue(program['programStartDate']),
      targetCompletionDate: this.dateInputValue(program['targetCompletionDate']),
      programOwner: program['programOwner'] ?? '',
      programOwnerKeycloakId: program['programOwnerKeycloakId'] ?? '',
      programStatus: program['programStatus'] ?? 'IN_PROGRESS',
    }, { emitEvent: false });
    this.refreshProgramDateValidators();
  }

  private patchImplementationDetails(implementation: RecordLike): void {
    const nested = this.asRecord(implementation['implementation']);
    const source = nested ? { ...implementation, ...nested } : implementation;
    const rawProtectionType = this.asString(source['protectionType']);
    const protectionType = rawProtectionType.toUpperCase() === 'NONE' ? 'None' : rawProtectionType;
    const algorithmOrMethod = this.algorithmLabel(source['algorithmOrMethod']);
    this.form.patchValue({
      implementationStatus: source['implementationStatus'] ?? 'IN_PROGRESS',
      protectionType,
      algorithmOrMethod,
      applicableStandards: this.toStringArray(source['applicableStandards']),
      implementationOwner: source['implementationOwner'] ?? '',
      implementationOwnerKeycloakId: source['implementationOwnerKeycloakId'] ?? '',
      plannedStartDate: this.dateInputValue(source['plannedStartDate']),
      plannedCompletionDate: this.dateInputValue(source['plannedCompletionDate']),
      actualImplementationDate: this.dateInputValue(source['actualImplementationDate']),
      implementationNotes: source['implementationNotes'] ?? '',
      implementationEvidenceUri: source['implementationEvidenceUri'] ?? '',
    }, { emitEvent: false });
    this.onProtectionTypeChanged();
    // The selected algorithm is restored after reconfiguring its validator.
    if (algorithmOrMethod && this.algorithmOptions.includes(algorithmOrMethod)) {
      this.form.patchValue({ algorithmOrMethod }, { emitEvent: false });
    }
    this.configureImplementationDateValidators();
  }

  private patchVerificationDetails(verification: RecordLike): void {
    const nested = this.asRecord(verification['verification']);
    const source = nested ? { ...verification, ...nested } : verification;
    this.form.patchValue({
      verificationStatus: source['verificationStatus'] ?? 'PASSED',
      verifiedBy: source['verifiedBy'] ?? '',
      verifiedByKeycloakId: source['verifiedByKeycloakId'] ?? '',
      verificationDate: this.dateInputValue(source['verificationDate']),
      verificationResult: source['verificationResult'] ?? '',
      verificationEvidenceUri: source['verificationEvidenceUri'] ?? '',
    }, { emitEvent: false });
  }

  private configureImplementationDateValidators(): void {
    const plannedStart = this.form.get('plannedStartDate');
    const plannedEnd = this.form.get('plannedCompletionDate');
    const actualDate = this.form.get('actualImplementationDate');

    if (this.isImplementationInProgress) {
      plannedStart?.setValidators([
        Validators.required,
        (control: AbstractControl) => this.validatePlannedDateRange(control),
      ]);
      plannedEnd?.setValidators([
        Validators.required,
        (control: AbstractControl) => this.validatePlannedDateRange(control),
        (control: AbstractControl) => this.validatePlannedCompletionOrder(control),
      ]);
      actualDate?.clearValidators();
    } else {
      actualDate?.setValidators([
        Validators.required,
        (control: AbstractControl) => this.validateActualImplementationDate(control),
      ]);
      plannedStart?.clearValidators();
      plannedEnd?.clearValidators();
    }

    plannedStart?.updateValueAndValidity({ emitEvent: false });
    plannedEnd?.updateValueAndValidity({ emitEvent: false });
    actualDate?.updateValueAndValidity({ emitEvent: false });
    this.refreshProgramDateValidators();
  }

  private refreshProgramDateValidators(): void {
    this.form.get('targetCompletionDate')?.updateValueAndValidity({ emitEvent: false });
    this.form.get('plannedStartDate')?.updateValueAndValidity({ emitEvent: false });
    this.form.get('plannedCompletionDate')?.updateValueAndValidity({ emitEvent: false });
  }

  private validateTargetCompletionDate(control: AbstractControl): ValidationErrors | null {
    const startDate = this.asString(control.parent?.get('programStartDate')?.value);
    const targetDate = this.asString(control.value);
    if (!startDate || !targetDate) return null;

    const minimum = this.addDaysToDateString(startDate, 1);
    return targetDate >= minimum ? null : { targetMustBeAfterStart: { minimum } };
  }

  private validatePlannedDateRange(control: AbstractControl): ValidationErrors | null {
    const value = this.asString(control.value);
    const startDate = this.asString(control.parent?.get('programStartDate')?.value);
    const targetDate = this.asString(control.parent?.get('targetCompletionDate')?.value);
    if (!value || !startDate || !targetDate) return null;

    return value >= startDate && value <= targetDate
      ? null
      : { outsideProgramWindow: { minimum: startDate, maximum: targetDate } };
  }

  private validatePlannedCompletionOrder(control: AbstractControl): ValidationErrors | null {
    const completionDate = this.asString(control.value);
    const plannedStartDate = this.asString(control.parent?.get('plannedStartDate')?.value);
    if (!completionDate || !plannedStartDate) return null;
    return completionDate >= plannedStartDate ? null : { beforePlannedStart: true };
  }

  private validateActualImplementationDate(control: AbstractControl): ValidationErrors | null {
    const value = this.asString(control.value);
    if (!value) return null;
    return value <= this.todayDate ? null : { futureImplementationDate: true };
  }

  private addDaysToDateString(value: string, days: number): string {
    if (!value) return '';
    const parts = value.split('-').map(Number);
    if (parts.length !== 3 || parts.some(part => !Number.isFinite(part))) return '';
    // Use UTC to avoid daylight-saving transitions changing the calendar day.
    const date = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    date.setUTCDate(date.getUTCDate() + days);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private validateControls(names: string[]): boolean {
    names.forEach(name => {
      const control = this.form.get(name);
      control?.markAsTouched();
      // Re-run validators at submit time, including the dynamic 'today' bound.
      control?.updateValueAndValidity({ emitEvent: false });
    });
    const invalid = names.some(name => !!this.form.get(name)?.invalid);
    if (invalid) {
      this.submitError = 'Complete the required fields before continuing.';
      return false;
    }
    return true;
  }

  private handleSaveError(stepName: string, error: unknown): void {
    console.error(`Unable to save ${stepName}:`, error);
    this.submitError = `Unable to save ${stepName}. Please review the fields and try again.`;
  }

  private async getCurrentUsername(): Promise<string> {
    try {
      const username = this.keycloakService.getUsername();
      if (username) return username;
      const profile = await this.keycloakService.loadUserProfile();
      return profile.username ?? profile.firstName ?? profile.email ?? '';
    } catch {
      return '';
    }
  }

  private getCurrentKeycloakUserId(): string {
    const stateId = this.keycloakStateService.getUserId();
    if (stateId) return stateId;
    try {
      const token = this.keycloakService.getKeycloakInstance().tokenParsed as RecordLike | undefined;
      return this.asString(token?.['sub']);
    } catch {
      return '';
    }
  }

  private findEntity(raw: unknown, candidateKeys: string[]): RecordLike | null {
    const rows = this.toRows(raw);
    const found = rows.find(row => candidateKeys.some(key => this.hasValue(row[key])));
    if (found) return found;

    const record = this.asRecord(raw);
    if (record) {
      for (const wrapperKey of ['program', 'implementation', 'verification', 'data', 'result']) {
        const wrapped = record[wrapperKey];
        if (Array.isArray(wrapped)) {
          const nested = wrapped.find(item => {
            const itemRecord = this.asRecord(item);
            return !!itemRecord && candidateKeys.some(key => this.hasValue(itemRecord[key]));
          });
          if (nested && typeof nested === 'object') return nested as RecordLike;
        } else {
          const nested = this.asRecord(wrapped);
          if (nested && candidateKeys.some(key => this.hasValue(nested[key]))) return nested;
        }
      }
    }
    return null;
  }

  private toRows(raw: unknown): RecordLike[] {
    if (Array.isArray(raw)) {
      return raw
        .filter(item => !!item && typeof item === 'object')
        .map(item => item as RecordLike);
    }
    const record = this.asRecord(raw);
    if (!record) return [];

    for (const key of ['Resource retrieved successfully', 'users', 'programs', 'items', 'content', 'data', 'results', 'records', 'implementation', 'verifications', 'verification']) {
      const value = record[key];
      if (Array.isArray(value)) {
        return value.filter(item => !!item && typeof item === 'object') as RecordLike[];
      }
    }
    for (const key of ['program', 'result']) {
      const value = this.asRecord(record[key]);
      if (value) return [value];
    }

    return Object.keys(record).some(key => this.hasValue(record[key])) ? [record] : [];
  }

  private asRecord(value: unknown): RecordLike | null {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? value as RecordLike
      : null;
  }

  private hasValue(value: unknown): boolean {
    return value !== null && value !== undefined && String(value).trim() !== '';
  }

  private asString(value: unknown): string {
    return value === null || value === undefined ? '' : String(value).trim();
  }

  private dateInputValue(value: unknown): string {
    const date = this.asString(value);
    return date ? date.slice(0, 10) : '';
  }

  private toStringArray(value: unknown): string[] {
    if (Array.isArray(value)) return value.map(item => this.asString(item)).filter(Boolean);
    const raw = this.asString(value);
    return raw ? raw.split(',').map(item => item.trim()).filter(Boolean) : [];
  }

  private toAlgorithmCode(labelOrCode: string): string {
    if (!labelOrCode) return '';
    if (this.protectionTypeAlgorithmMapping[labelOrCode]) return this.protectionTypeAlgorithmMapping[labelOrCode];
    return labelOrCode;
  }

  private algorithmLabel(codeOrLabel: unknown): string {
    const raw = this.asString(codeOrLabel);
    if (!raw) return '';
    const entry = Object.entries(this.protectionTypeAlgorithmMapping)
      .find(([, code]) => code === raw);
    return entry?.[0] ?? raw;
  }

  private readNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  }

  private applyImplementationStatusRules(): void {
    this.configureImplementationDateValidators();
    this.onProtectionTypeChanged();
  }

  private refreshView(): void {
    this.changeDetector.markForCheck();
  }
}
